import * as Sentry from '@sentry/react';
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/contexts/AuthContext';
import { ALL_MODULES } from '@/lib/constants';
import { adminApi, billingApi, whatsappApi } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import {
  Bell,
  Shield,
  Database,
  Check,
  Plus,
  Loader2,
  AlertTriangle,
  Settings as SettingsIcon,
  Circle,
  MessageSquare,
  Clock,
  Ban,
  Trash2,
  Edit2,
  Ticket,
  Building2,
  QrCode,
  Copy,
  ExternalLink,
  BellRing,
  MapPin,
  Crosshair,
  Smartphone,
  CalendarClock
} from 'lucide-react';
import * as Icons from 'lucide-react';
import { cn } from '@/lib/utils';
import { getCurrentCoordinates } from '@/lib/device-utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MembershipPlan } from '@/types';
import { gymApi } from '@/lib/api';
import { SaasTier } from '@/types';
import { requestPushPermission } from '@/lib/onesignal';

export default function SettingsPage() {
  const { user, gym, hasRole, refreshAuth } = useAuth();
  const isSuperAdmin = hasRole(['super_admin']);
  const isGymAdmin = hasRole(['gym_admin']);
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'general';

  const [enabledModules, setEnabledModules] = useState<string[]>(gym?.modules_enabled || []);
  const [isUpdatingModules, setIsUpdatingModules] = useState(false);
  const [whatsappAddon, setWhatsappAddon] = useState<any>(null);
  const [isLoadingAddon, setIsLoadingAddon] = useState(false);

  // Membership Plans State
  const [plans, setPlans] = useState<MembershipPlan[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [isPlanDialogOpen, setIsPlanDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Partial<MembershipPlan> | null>(null);
  const [isSavingPlan, setIsSavingPlan] = useState(false);
  const [isDeactivatingWhatsapp, setIsDeactivatingWhatsapp] = useState(false);
  const [isDeactivatingDialogOpen, setIsDeactivatingDialogOpen] = useState(false);
  const [isConfirmingSubscriptionChange, setIsConfirmingSubscriptionChange] = useState(false);

  // Gym Integration Settings (per gym)
  const [twilioAccountSid, setTwilioAccountSid] = useState(gym?.twilio_account_sid || '');
  const [twilioAuthToken, setTwilioAuthToken] = useState(gym?.twilio_auth_token || '');
  const [twilioPhoneNumber, setTwilioPhoneNumber] = useState(gym?.twilio_phone_number || '');
  const [openaiApiKey, setOpenaiApiKey] = useState(gym?.openai_api_key || '');
  const [isSavingIntegrations, setIsSavingIntegrations] = useState(false);

  // Gym Profile State
  const [gymName, setGymName] = useState(gym?.name || '');
  const [gymAddress, setGymAddress] = useState(gym?.address || '');
  const [gymPhone, setGymPhone] = useState(gym?.phone || '');
  const [gymTimezone, setGymTimezone] = useState(gym?.timezone || 'UTC');
  const [gymOpeningHours, setGymOpeningHours] = useState(gym?.settings?.opening_hours || '06:00');
  const [gymClosingHours, setGymClosingHours] = useState(gym?.settings?.closing_hours || '22:00');
  const [strictQrValidation, setStrictQrValidation] = useState(gym?.settings?.strict_qr_validation ?? true);
  const [dailySummaryEnabled, setDailySummaryEnabled] = useState(gym?.settings?.daily_summary_enabled ?? false);
  const [geofenceEnabled, setGeofenceEnabled] = useState(gym?.settings?.geofence_enabled ?? false);
  const [geofenceRadius, setGeofenceRadius] = useState(String(gym?.settings?.geofence_radius_m ?? 100));
  const [deviceBindingEnabled, setDeviceBindingEnabled] = useState(gym?.settings?.device_binding_enabled ?? true);
  // Reservas de citas con la nutricionista
  const [nutritionBookingEnabled, setNutritionBookingEnabled] = useState(gym?.settings?.nutrition_booking_enabled ?? false);
  const [nutritionistName, setNutritionistName] = useState(gym?.settings?.nutritionist_name || '');
  const [nutritionAdvanceHours, setNutritionAdvanceHours] = useState(String(gym?.settings?.nutrition_booking_advance_hours ?? 24));
  const [nutritionMaxDays, setNutritionMaxDays] = useState(String(gym?.settings?.nutrition_booking_max_days ?? 60));
  const [nutritionGraceMinutes, setNutritionGraceMinutes] = useState(String(gym?.settings?.nutrition_no_show_grace_minutes ?? 10));
  const [nutritionCancellationPolicy, setNutritionCancellationPolicy] = useState(gym?.settings?.nutrition_cancellation_policy || '');
  const [isSavingNutritionSettings, setIsSavingNutritionSettings] = useState(false);
  const [gymLatitude, setGymLatitude] = useState(gym?.latitude != null ? String(gym.latitude) : '');
  const [gymLongitude, setGymLongitude] = useState(gym?.longitude != null ? String(gym.longitude) : '');
  const [isLocatingGym, setIsLocatingGym] = useState(false);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [saasTiers, setSaasTiers] = useState<SaasTier[]>([]);
  const [selectedTierId, setSelectedTierId] = useState(gym?.tier_id || '');
  const [isLoadingTiers, setIsLoadingTiers] = useState(false);

  // Billing cycle (monthly/annual) state
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>(
    (gym?.billing_cycle as 'monthly' | 'annual') || 'monthly'
  );
  const [isUpdatingBillingCycle, setIsUpdatingBillingCycle] = useState(false);

  // Push notification permission state
  const [pushPermission, setPushPermission] = useState<NotificationPermission>('default');
  const [isRequestingPush, setIsRequestingPush] = useState(false);

  useEffect(() => {
    Sentry.setTag("area", "system");
  }, []);

  // Sincronizar estado del permiso de notificaciones push
  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    setPushPermission(Notification.permission);
  }, []);

  const handleRequestPushPermission = async () => {
    setIsRequestingPush(true);
    try {
      const finalPerm = await requestPushPermission(12000);
      setPushPermission(finalPerm);
      toast({
        title: finalPerm === 'granted' ? 'Notificaciones activadas' : 'Permiso no concedido',
        description: finalPerm === 'granted'
          ? 'Recibirás alertas push de nuevas suscripciones y renovaciones.'
          : 'El permiso fue bloqueado o denegado. Actívalo desde la configuración del navegador.',
        variant: finalPerm === 'granted' ? 'default' : 'destructive',
      });
    } catch (error: any) {
      toast({ title: 'Error', description: error?.message || 'No se pudo solicitar el permiso', variant: 'destructive' });
    } finally {
      setIsRequestingPush(false);
    }
  };

  useEffect(() => {
    // Reset gym-specific state to prevent showing data from previous gym
    if (gym?.gym_id) {
      setEnabledModules(gym.modules_enabled || []);
      setWhatsappAddon(null);
      setPlans([]);
      setGymName(gym.name || '');
      setGymAddress(gym.address || '');
      setGymPhone(gym.phone || '');
      setGymTimezone(gym.timezone || 'UTC');
      setGymOpeningHours(gym.settings?.opening_hours || '06:00');
      setGymClosingHours(gym.settings?.closing_hours || '22:00');
      setStrictQrValidation(gym.settings?.strict_qr_validation ?? true);
      setDailySummaryEnabled(gym.settings?.daily_summary_enabled ?? false);
      setGeofenceEnabled(gym.settings?.geofence_enabled ?? false);
      setGeofenceRadius(String(gym.settings?.geofence_radius_m ?? 100));
      setDeviceBindingEnabled(gym.settings?.device_binding_enabled ?? true);
      setNutritionBookingEnabled(gym.settings?.nutrition_booking_enabled ?? false);
      setNutritionistName(gym.settings?.nutritionist_name || '');
      setNutritionAdvanceHours(String(gym.settings?.nutrition_booking_advance_hours ?? 24));
      setNutritionMaxDays(String(gym.settings?.nutrition_booking_max_days ?? 60));
      setNutritionGraceMinutes(String(gym.settings?.nutrition_no_show_grace_minutes ?? 10));
      setNutritionCancellationPolicy(gym.settings?.nutrition_cancellation_policy || '');
      setGymLatitude(gym.latitude != null ? String(gym.latitude) : '');
      setGymLongitude(gym.longitude != null ? String(gym.longitude) : '');

      // Integration fields
      setTwilioAccountSid(gym.twilio_account_sid || '');
      setTwilioAuthToken(gym.twilio_auth_token || '');
      setTwilioPhoneNumber(gym.twilio_phone_number || '');
      setOpenaiApiKey(gym.openai_api_key || '');

      setSelectedTierId(gym.tier_id || '');
      setBillingCycle((gym.billing_cycle as 'monthly' | 'annual') || 'monthly');

      fetchSaasTiers();
      fetchAddonStatus();
      fetchPlans();
    } else {
      setEnabledModules([]);
      setWhatsappAddon(null);
      setPlans([]);
      setGymName('');
      setGymAddress('');
      setGymPhone('');
      setGymTimezone('UTC');
      setGymOpeningHours('06:00');
      setGymClosingHours('22:00');
      setStrictQrValidation(true);
      setDailySummaryEnabled(false);
      setGeofenceEnabled(false);
      setGeofenceRadius('100');
      setDeviceBindingEnabled(true);
      setNutritionBookingEnabled(false);
      setNutritionistName('');
      setNutritionAdvanceHours('24');
      setNutritionMaxDays('60');
      setNutritionGraceMinutes('10');
      setNutritionCancellationPolicy('');
      setGymLatitude('');
      setGymLongitude('');
      setTwilioAccountSid('');
      setTwilioAuthToken('');
      setTwilioPhoneNumber('');
      setOpenaiApiKey('');
      setSelectedTierId('');
    }
  }, [gym?.gym_id]);

  const fetchSaasTiers = async () => {
    setIsLoadingTiers(true);
    try {
      const { data, error } = await supabase
        .from('saas_tiers')
        .select('*')
        .order('price', { ascending: true });
      if (data) {
        const visible = isSuperAdmin
          ? data
          : data.filter((t: SaasTier) => !t.is_private || t.id === gym?.tier_id);
        setSaasTiers(visible);
      }
    } catch (error: any) {
      console.error('Error fetching tiers:', error);
      Sentry.captureException(error, {
        tags: { operation: 'fetch_saas_tiers' }
      });
    } finally {
      setIsLoadingTiers(false);
    }
  };

  const handleUpdateIntegrations = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!gym?.gym_id) return;
    setIsSavingIntegrations(true);
    try {
      const res = await adminApi.updateGym({
        gym_id: gym.gym_id,
        twilio_account_sid: twilioAccountSid,
        twilio_auth_token: twilioAuthToken,
        twilio_phone_number: twilioPhoneNumber,
        openai_api_key: openaiApiKey,
      });
      if (res.success) {
        await refreshAuth();
        toast({ title: 'Configuración guardada', description: 'Las llaves de integración para este gimnasio se han actualizado.' });
      }
    } catch (error: any) {
      console.error("Error updating integrations:", error);
      Sentry.captureException(error, {
        tags: { operation: 'update_integrations' },
        extra: { gym_id: gym.gym_id }
      });
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setIsSavingIntegrations(false);
    }
  };

  const handleCaptureGymLocation = async () => {
    setIsLocatingGym(true);
    try {
      const coords = await getCurrentCoordinates(15000);
      setGymLatitude(coords.latitude.toFixed(6));
      setGymLongitude(coords.longitude.toFixed(6));
      toast({
        title: 'Ubicación capturada',
        description: `Precisión aproximada: ${Math.round(coords.accuracy)} m. Guarda los cambios para aplicarla.`,
      });
    } catch (error: any) {
      const message =
        error?.code === 1
          ? 'Permite el acceso a tu ubicación en el navegador e intenta de nuevo.'
          : error?.code === 3
            ? 'No se obtuvo la ubicación a tiempo. Intenta de nuevo.'
            : 'No se pudo obtener tu ubicación actual.';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setIsLocatingGym(false);
    }
  };

  const handleUpdateGymProfile = async () => {
    if (!gym?.gym_id) return;

    const latitude = gymLatitude.trim() === '' ? null : Number(gymLatitude);
    const longitude = gymLongitude.trim() === '' ? null : Number(gymLongitude);
    const radius = Math.min(500, Math.max(20, Number(geofenceRadius) || 100));

    if (
      (latitude !== null && (!Number.isFinite(latitude) || latitude < -90 || latitude > 90)) ||
      (longitude !== null && (!Number.isFinite(longitude) || longitude < -180 || longitude > 180))
    ) {
      toast({
        title: 'Coordenadas inválidas',
        description: 'La latitud debe estar entre -90 y 90 y la longitud entre -180 y 180.',
        variant: 'destructive',
      });
      return;
    }

    if (geofenceEnabled && (latitude === null || longitude === null)) {
      toast({
        title: 'Faltan las coordenadas',
        description: 'Captura la ubicación del gimnasio o ingresa latitud y longitud para activar el geofence.',
        variant: 'destructive',
      });
      return;
    }

    setIsUpdatingProfile(true);
    try {
      const res = await adminApi.updateGym({
        gym_id: gym.gym_id,
        name: isSuperAdmin ? gymName : undefined, // Only super admin can change name
        address: gymAddress,
        phone: gymPhone,
        timezone: gymTimezone,
        latitude,
        longitude,
        settings: {
          ...gym.settings,
          opening_hours: gymOpeningHours,
          closing_hours: gymClosingHours,
          strict_qr_validation: strictQrValidation,
          daily_summary_enabled: dailySummaryEnabled,
          geofence_enabled: geofenceEnabled,
          geofence_radius_m: radius,
          device_binding_enabled: deviceBindingEnabled
        }
      });
      if (res.success) {
        await refreshAuth();
        toast({ title: 'Perfil actualizado', description: 'La información del gimnasio se ha guardado.' });
      }
    } catch (error: any) {
      console.error("Error updating gym profile:", error);
      Sentry.captureException(error, {
        tags: { operation: 'update_gym_profile' },
        extra: { gym_id: gym.gym_id, gymName }
      });
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const fetchPlans = async () => {
    setIsLoadingPlans(true);
    try {
      const res = await gymApi.listMembershipPlans(gym?.gym_id);
      if (res.success) setPlans(res.plans);
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const handleSavePlan = async () => {
    if (!gym?.gym_id && !isSuperAdmin) return;
    if (!editingPlan?.name) return;

    if (!editingPlan.id && plans.length >= 8) {
      toast({
        title: 'Límite alcanzado',
        description: 'Solo puedes tener hasta 8 tipos de membresías activos.',
        variant: 'destructive',
      });
      return;
    }

    setIsSavingPlan(true);
    try {
      if (editingPlan.id) {
        await gymApi.updateMembershipPlan(editingPlan.id, {
          name: editingPlan.name,
          price: editingPlan.price,
          duration_days: editingPlan.duration_days,
          description: editingPlan.description,
          is_private: editingPlan.is_private || false,
          included_modules: editingPlan.included_modules || []
        });
        toast({ title: 'Plan actualizado', description: 'Los cambios se han guardado.' });
      } else {
        await gymApi.createMembershipPlan({
          name: editingPlan.name,
          price: editingPlan.price || 0,
          duration_days: editingPlan.duration_days || 30,
          description: editingPlan.description,
          included_modules: editingPlan.included_modules || [],
          is_private: editingPlan.is_private || false,
          gym_id: gym?.gym_id || null
        });
        toast({ title: 'Plan creado', description: 'El nuevo plan se ha guardado.' });
      }
      setIsPlanDialogOpen(false);
      fetchPlans();
    } catch (error: any) {
      console.error("Error saving membership plan:", error);
      Sentry.captureException(error, {
        tags: { operation: 'save_membership_plan' },
        extra: { gym_id: gym?.gym_id, plan_name: editingPlan.name }
      });
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setIsSavingPlan(false);
    }
  };

  const handleDeletePlan = async (id: string) => {
    if (!window.confirm('¿Estás seguro de eliminar este plan? Esto no afectará a las membresías ya existentes.')) return;
    try {
      await gymApi.deleteMembershipPlan(id);
      toast({ title: 'Plan eliminado' });
      fetchPlans();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const fetchAddonStatus = async () => {
    if (!gym?.gym_id) return;
    setIsLoadingAddon(true);
    try {
      const res = await whatsappApi.getAddonStatus(gym.gym_id);
      if (res.success) setWhatsappAddon(res.addon);
    } finally {
      setIsLoadingAddon(false);
    }
  };

  const calculatePriceForTier = (tierId: string, modules: string[]) => {
    const targetTier = saasTiers.find(t => t.id === tierId);
    if (!targetTier) return 0;

    const basePrice = Number(targetTier.price);
    const includedModules = targetTier.included_modules || [];

    // Calculate modular cost only for modules NOT included in the tier
    const extraModulesPrice = modules.reduce((sum, mId) => {
      // Don't charge for webhooks if not approved, even if in modules list
      if (mId === 'webhooks' && whatsappAddon?.status !== 'approved') return sum;

      if (includedModules.includes(mId)) return sum;
      const mod = ALL_MODULES.find(m => m.module_id === mId);
      return sum + (mod?.price || 0);
    }, 0);

    let total = basePrice + extraModulesPrice;

    // Special case for WhatsApp (webhooks) if approved but not manually enabled in the list
    if (whatsappAddon?.status === 'approved' && !modules.includes('webhooks') && !includedModules.includes('webhooks')) {
      total += 25;
    }

    return Math.round(total * 100) / 100;
  };

  const calculateProratedCharge = (newMonthlyPrice: number) => {
    if (!gym?.expiry_date || !gym.price) return 0;

    const now = new Date();
    const expiry = new Date(gym.expiry_date);

    const diffTime = expiry.getTime() - now.getTime();
    const remainingDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (remainingDays <= 0) return 0;

    // Calculate the difference in monthly price
    const delta = newMonthlyPrice - gym.price;

    // Special case: if remaining days is very close to a full month, use full delta
    if (remainingDays >= 30) return Math.round(delta * 100) / 100;

    // Calculate prorated amount for the remaining days
    const prorated = (delta / 30) * remainingDays;
    return Math.round(prorated * 100) / 100;
  };

  const calculateTotalPrice = (modules: string[]) => {
    return calculatePriceForTier(selectedTierId, modules);
  };

  const currentTotal = calculateTotalPrice(enabledModules);
  const proratedCharge = calculateProratedCharge(currentTotal);

  const hasNutritionModule = enabledModules.includes('nutricion');

  const annualDiscountedTotal = Math.round(currentTotal * 12 * 0.8 * 100) / 100;
  const annualSavings = Math.round(currentTotal * 12 * 0.2 * 100) / 100;

  const handleBillingCycleChange = async (cycle: 'monthly' | 'annual') => {
    if (!gym?.gym_id || cycle === billingCycle) return;
    setIsUpdatingBillingCycle(true);
    try {
      await billingApi.updateBillingCycle(gym.gym_id, cycle);
      setBillingCycle(cycle);
      toast({
        title: 'Ciclo de facturación actualizado',
        description: cycle === 'annual'
          ? `Pago anual activado. Ahorrás $${annualSavings.toFixed(2)} al año.`
          : 'Pago mensual activado.',
      });
      await refreshAuth();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.message || 'No se pudo cambiar el ciclo', variant: 'destructive' });
    } finally {
      setIsUpdatingBillingCycle(false);
    }
  };

  const hasSubscriptionChanges =
    JSON.stringify([...enabledModules].sort()) !== JSON.stringify([...(gym?.modules_enabled || [])].sort()) ||
    selectedTierId !== (gym?.tier_id || '');

  const toggleModule = (moduleId: string) => {
    // If module is included in current tier, don't allow toggling
    const currentTier = saasTiers.find(t => t.id === selectedTierId) || gym?.tier;
    if (currentTier?.included_modules?.includes(moduleId)) {
      toast({
        title: 'Módulo incluido',
        description: `Este módulo ya forma parte del plan ${currentTier.name}.`,
      });
      return;
    }

    setEnabledModules(prev =>
      prev.includes(moduleId)
        ? prev.filter(m => m !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handleUpdateAddons = () => {
    setIsConfirmingSubscriptionChange(true);
  };

  const executeSubscriptionChange = async () => {
    if (!gym?.gym_id) {
      toast({ title: 'Error', description: 'No se ha seleccionado un gimnasio para actualizar.', variant: 'destructive' });
      return;
    }
    setIsUpdatingModules(true);
    setIsConfirmingSubscriptionChange(false);
    try {
      const currentTier = saasTiers.find(t => t.id === selectedTierId) || gym.tier;
      const tierModules = currentTier?.included_modules || [];

      // Combine manual modules with tier modules
      const combinedModules = Array.from(new Set([...tierModules, ...enabledModules]));

      const whatsappInTotal = whatsappAddon?.status === 'approved' && !combinedModules.includes('webhooks');
      const finalModules = whatsappInTotal ? [...combinedModules, 'webhooks'] : combinedModules;

      const newPrice = calculateTotalPrice(enabledModules);
      const proratedAdjustment = calculateProratedCharge(newPrice);
      const newCreditBalance = (gym?.credit_balance || 0) - proratedAdjustment;

      const res = await adminApi.updateGym({
        gym_id: gym.gym_id,
        modules_enabled: finalModules,
        price: newPrice,
        tier_id: selectedTierId,
        plan: currentTier?.name,
        credit_balance: newCreditBalance
      });

      if (res.success) {
        setEnabledModules(finalModules);
        await refreshAuth();
        const adjText = proratedAdjustment > 0
          ? `Se ha descontado un cargo de ajuste de $${proratedAdjustment.toFixed(2)}.`
          : `Se ha aplicado un crédito de $${Math.abs(proratedAdjustment).toFixed(2)}.`;

        toast({
          title: 'Suscripción actualizada',
          description: `Tu nuevo plan y addons han sido configurados por $${newPrice.toFixed(2)}. ${adjText}`
        });
      }
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'No se pudieron actualizar los addons', variant: 'destructive' });
    } finally {
      setIsUpdatingModules(false);
    }
  };

  const handleRequestWhatsapp = async () => {
    if (!gym?.gym_id) return;
    setIsLoadingAddon(true);
    try {
      await whatsappApi.requestAddon(gym.gym_id);
      toast({ title: 'Solicitud enviada', description: 'El super admin revisará tu solicitud para el asistente de WhatsApp.' });
      fetchAddonStatus();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setIsLoadingAddon(false);
    }
  };

  const handleDirectActivate = async () => {
    if (!gym?.gym_id || !isSuperAdmin) return;
    setIsLoadingAddon(true);
    try {
      await whatsappApi.directActivate(gym.gym_id);
      const newModules = enabledModules.includes('webhooks') ? enabledModules : [...enabledModules, 'webhooks'];
      setEnabledModules(newModules);
      const newTier = saasTiers.find(t => t.id === selectedTierId) || gym.tier;
      await adminApi.updateGym({
        gym_id: gym.gym_id,
        modules_enabled: newModules,
        price: calculateTotalPrice(newModules),
        tier_id: selectedTierId,
        plan: newTier?.name
      });
      toast({ title: 'Addon activado', description: 'El asistente de WhatsApp se ha activado instantáneamente.' });
      fetchAddonStatus();
      refreshAuth();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setIsLoadingAddon(false);
    }
  };

  const handleApproveAddon = async (id: string, status: 'approved' | 'rejected') => {
    if (!isSuperAdmin) return;
    setIsLoadingAddon(true);
    try {
      await whatsappApi.approveAddon(id, status);
      if (status === 'approved') {
        const newModules = enabledModules.includes('webhooks') ? enabledModules : [...enabledModules, 'webhooks'];
        setEnabledModules(newModules);
        const newTier = saasTiers.find(t => t.id === selectedTierId) || gym.tier;
        await adminApi.updateGym({
          gym_id: gym.gym_id,
          modules_enabled: newModules,
          price: calculateTotalPrice(newModules),
          tier_id: selectedTierId,
          plan: newTier?.name
        });
      }
      toast({ title: status === 'approved' ? 'Solicitud aprobada' : 'Solicitud rechazada', description: status === 'approved' ? 'El addon ahora está activo.' : 'La solicitud ha sido declinada.' });
      fetchAddonStatus();
      refreshAuth();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setIsLoadingAddon(false);
    }
  };

  const handleSaveNutritionBooking = async () => {
    if (!gym?.gym_id) return;
    setIsSavingNutritionSettings(true);
    try {
      await adminApi.updateGym({
        gym_id: gym.gym_id,
        settings: {
          ...(gym.settings || {}),
          nutrition_booking_enabled: nutritionBookingEnabled,
          nutritionist_name: nutritionistName.trim(),
          nutrition_booking_advance_hours: Number(nutritionAdvanceHours) || 24,
          nutrition_booking_max_days: Number(nutritionMaxDays) || 60,
          nutrition_no_show_grace_minutes: Number(nutritionGraceMinutes) || 10,
          nutrition_cancellation_policy: nutritionCancellationPolicy.trim(),
        },
      });
      await refreshAuth();
      toast({
        title: 'Configuración guardada',
        description: 'Los ajustes de reservas de Nutrición quedaron actualizados.',
      });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setIsSavingNutritionSettings(false);
    }
  };

  const handleDeactivateWhatsapp = async () => {    if (!whatsappAddon?.id || !gym?.gym_id) return;
    setIsDeactivatingWhatsapp(true);
    try {
      // 1. Deactivate the addon record
      await whatsappApi.deactivateAddon(whatsappAddon.id);

      // 2. Calculate new state for the gym
      const newModules = enabledModules.filter(m => m !== 'webhooks');
      const newPrice = calculatePriceForTier(selectedTierId, newModules);
      const creditAmount = calculateProratedCharge(newPrice);
      const newBalance = (gym?.credit_balance || 0) - creditAmount;

      // 3. Update the gym record immediately
      await adminApi.updateGym({
        gym_id: gym.gym_id,
        modules_enabled: newModules,
        price: newPrice,
        credit_balance: newBalance
      });

      setIsDeactivatingDialogOpen(false);
      setEnabledModules(newModules);

      toast({
        title: 'Servicio Desactivado',
        description: `El Asistente de WhatsApp ha sido desactivado. Se ha aplicado un crédito de $${Math.abs(creditAmount).toFixed(2)} por los días restantes.`
      });

      await refreshAuth();
      fetchAddonStatus();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudo desactivar el servicio: ' + error.message,
        variant: 'destructive'
      });
    } finally {
      setIsDeactivatingWhatsapp(false);
    }
  };

  return (
    <DashboardLayout
      title="Configuración"
      subtitle="Administra las preferencias del sistema"
    >
      <Tabs
        defaultValue={defaultTab}
        onValueChange={(val) => setSearchParams({ tab: val })}
        className="w-full"
      >
        <TabsList className="mb-6 bg-muted/50 p-1 rounded-xl w-full flex overflow-x-auto justify-start md:justify-center no-scrollbar">
          <TabsTrigger value="general" className="rounded-lg font-bold px-4 md:px-6 flex-shrink-0">General</TabsTrigger>
          <TabsTrigger value="membresias" className="rounded-lg font-bold px-4 md:px-6 flex-shrink-0">Membresías</TabsTrigger>
          {isSuperAdmin && <TabsTrigger value="sistema" className="rounded-lg font-bold px-4 md:px-6 flex-shrink-0">Sistema</TabsTrigger>}
        </TabsList>

        <TabsContent value="general" className="mt-0 space-y-6">
          <div className="columns-1 md:columns-2 xl:columns-3 gap-6 space-y-0">
            {/* Gym Profile */}
            <div className="break-inside-avoid mb-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-primary">
                    <Building2 className="h-5 w-5" />
                    Información del Gimnasio
                  </CardTitle>
                  <CardDescription>
                    Datos públicos que utiliza la IA para atender a tus clientes
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="gym-name">Nombre del Gimnasio</Label>
                      <Input
                        id="gym-name"
                        value={gymName}
                        onChange={(e) => setGymName(e.target.value)}
                        disabled={!isSuperAdmin}
                        className={cn(!isSuperAdmin && "bg-muted/50 cursor-not-allowed")}
                      />
                      {!isSuperAdmin && (
                        <p className="text-[10px] text-muted-foreground italic">
                          Solo los administradores del sistema pueden cambiar el nombre.
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="gym-phone">Teléfono de Contacto</Label>
                      <Input
                        id="gym-phone"
                        placeholder="Ej: +52 123 456 7890"
                        value={gymPhone}
                        onChange={(e) => setGymPhone(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="gym-address">Dirección Física</Label>
                    <Input
                      id="gym-address"
                      placeholder="Ej: Av. Principal 123, Col. Centro"
                      value={gymAddress}
                      onChange={(e) => setGymAddress(e.target.value)}
                    />
                    <p className="text-[10px] text-muted-foreground italic">
                      Esta dirección será usada por la IA para informar sobre la ubicación física.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="gym-timezone">Zona Horaria</Label>
                      <Select value={gymTimezone} onValueChange={setGymTimezone}>
                        <SelectTrigger id="gym-timezone">
                          <SelectValue placeholder="Seleccionar zona horaria" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="America/El_Salvador">El Salvador (UTC-6)</SelectItem>
                          <SelectItem value="America/Guatemala">Guatemala (UTC-6)</SelectItem>
                          <SelectItem value="America/Mexico_City">México Centro (UTC-6)</SelectItem>
                          <SelectItem value="America/Costa_Rica">Costa Rica (UTC-6)</SelectItem>
                          <SelectItem value="America/Panama">Panamá (UTC-5)</SelectItem>
                          <SelectItem value="America/Bogota">Colombia (UTC-5)</SelectItem>
                          <SelectItem value="America/Lima">Perú (UTC-5)</SelectItem>
                          <SelectItem value="America/Santiago">Chile (UTC-4)</SelectItem>
                          <SelectItem value="America/Argentina/Buenos_Aires">Argentina (UTC-3)</SelectItem>
                          <SelectItem value="America/New_York">Eastern US (UTC-5)</SelectItem>
                          <SelectItem value="America/Chicago">Central US (UTC-6)</SelectItem>
                          <SelectItem value="UTC">UTC</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-muted-foreground italic">
                        Define la zona horaria para cálculos de fechas y check-ins.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Clock className="h-3 w-3" /> Apertura
                      </Label>
                      <Input
                        type="time"
                        value={gymOpeningHours}
                        onChange={(e) => setGymOpeningHours(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Clock className="h-3 w-3" /> Cierre
                      </Label>
                      <Input
                        type="time"
                        value={gymClosingHours}
                        onChange={(e) => setGymClosingHours(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="border-t pt-4 space-y-4">
                    <div>
                      <p className="text-sm font-bold flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-primary" /> Check-in automático
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Controles de seguridad para la asistencia desde el enlace público del gimnasio.
                      </p>
                    </div>

                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium flex items-center gap-2">
                          <Smartphone className="h-3.5 w-3.5" /> Validación de dispositivo
                          <Badge
                            variant={deviceBindingEnabled ? 'default' : 'secondary'}
                            className="text-[10px] font-bold uppercase tracking-wide"
                          >
                            {deviceBindingEnabled ? 'Obligatorio' : 'Opcional'}
                          </Badge>
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {deviceBindingEnabled
                            ? 'Obligatorio: el primer check-in vincula el navegador del socio y otros dispositivos no podrán marcar su asistencia. El staff puede reasignarlo desde Clientes o registrar la asistencia con el escáner.'
                            : 'Opcional: cualquier navegador puede marcar la asistencia del socio, sin vincular un dispositivo.'}
                        </p>
                      </div>
                      <Switch
                        checked={deviceBindingEnabled}
                        onCheckedChange={setDeviceBindingEnabled}
                      />
                    </div>

                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium">Exigir ubicación (geofence)</p>
                        <p className="text-xs text-muted-foreground">
                          El socio debe compartir su GPS y estar dentro del radio permitido para marcar asistencia. El staff autenticado queda exento.
                        </p>
                      </div>
                      <Switch
                        checked={geofenceEnabled}
                        onCheckedChange={setGeofenceEnabled}
                      />
                    </div>

                    {geofenceEnabled && (
                      <div className="space-y-4 rounded-xl border border-dashed border-border p-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="gym-latitude">Latitud</Label>
                            <Input
                              id="gym-latitude"
                              type="number"
                              step="0.000001"
                              placeholder="13.692940"
                              value={gymLatitude}
                              onChange={(e) => setGymLatitude(e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="gym-longitude">Longitud</Label>
                            <Input
                              id="gym-longitude"
                              type="number"
                              step="0.000001"
                              placeholder="-89.218191"
                              value={gymLongitude}
                              onChange={(e) => setGymLongitude(e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="gym-geofence-radius">Radio (metros)</Label>
                            <Input
                              id="gym-geofence-radius"
                              type="number"
                              min={20}
                              max={500}
                              value={geofenceRadius}
                              onChange={(e) => setGeofenceRadius(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleCaptureGymLocation}
                            disabled={isLocatingGym}
                            className="w-full sm:w-auto"
                          >
                            {isLocatingGym ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Crosshair className="h-4 w-4 mr-2" />
                            )}
                            Usar mi ubicación actual
                          </Button>
                          <p className="text-[10px] text-muted-foreground italic">
                            Captura las coordenadas estando físicamente en el gimnasio (precisión recomendada ≤ 50 m).
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="pt-4 flex justify-end">
                    <Button
                      onClick={handleUpdateGymProfile}
                      disabled={isUpdatingProfile}
                      className="font-bold rounded-full px-8 shadow-lg shadow-primary/20"
                    >
                      {isUpdatingProfile ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Guardando...
                        </>
                      ) : 'Guardar Cambios'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="break-inside-avoid mb-6">
              {/* Technical Info (Isomed from AI) */}
              <Card className="bg-muted/30 border-dashed">
                <CardHeader className="pb-3">
                  <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                    <Database className="h-3.5 w-3.5" /> Información Técnica
                  </CardTitle>
                  <CardDescription className="text-[10px]">
                    Identificadores internos del sistema (No compartidos con la IA)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-2">
                    <Label className="text-[10px] text-muted-foreground font-bold">GYM ID</Label>
                    <div className="flex items-center gap-2 group">
                      <code className="flex-1 bg-zinc-950 border border-zinc-800 p-2 rounded-lg text-[11px] font-mono text-primary/80 break-all select-all">
                        {gym?.gym_id}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0 rounded-lg hover:bg-primary/10 hover:text-primary transition-all"
                        onClick={() => {
                          if (gym?.gym_id) {
                            navigator.clipboard.writeText(gym.gym_id);
                            toast({
                              title: "ID Copiado",
                              description: "Identificador copiado para soporte técnico.",
                            });
                          }
                        }}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="break-inside-avoid mb-6">
              {/* Notifications */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-primary">
                    <Bell className="h-5 w-5" />
                    Notificaciones
                  </CardTitle>
                  <CardDescription>
                    Configura las alertas del sistema
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">Alertas de vencimiento</p>
                      <p className="text-xs text-muted-foreground">
                        Notificar membresías por vencer
                      </p>
                    </div>
                    <Switch
                      checked={!user?.alerts_disabled}
                      onCheckedChange={async (checked) => {
                        try {
                          await gymApi.updateAlertPreference(user!.user_id, !checked);
                          await refreshAuth();
                          toast({
                            title: checked ? 'Alertas activadas' : 'Alertas desactivadas',
                            description: checked
                              ? 'Recibirás notificaciones de membresías por vencer.'
                              : 'Ya no verás alertas de membresías vencidas.',
                          });
                        } catch (error: any) {
                          toast({ title: 'Error', description: error.message, variant: 'destructive' });
                        }
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">Check-ins denegados</p>
                      <p className="text-xs text-muted-foreground">
                        Alertar intentos de acceso fallidos
                      </p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">Resumen diario</p>
                      <p className="text-xs text-muted-foreground">
                        Recibir estadísticas por email
                      </p>
                    </div>
                    <Switch
                      checked={dailySummaryEnabled}
                      onCheckedChange={async (checked) => {
                        setDailySummaryEnabled(checked);
                        if (!gym?.gym_id) return;
                        try {
                          await adminApi.updateGym({
                            gym_id: gym.gym_id,
                            settings: {
                              ...(gym.settings || {}),
                              daily_summary_enabled: checked
                            }
                          });
                          await refreshAuth();
                          toast({
                            title: checked ? 'Resumen activado' : 'Resumen desactivado',
                            description: checked
                              ? 'Recibirás un email diario con las estadísticas de tu gimnasio.'
                              : 'Ya no recibirás el resumen diario por email.'
                          });
                        } catch (error: any) {
                          toast({ title: 'Error', description: error.message, variant: 'destructive' });
                        }
                      }}
/>
                  </div>
                  {(isGymAdmin || isSuperAdmin) && (
                    <div className="flex items-center justify-between pt-3 border-t">
                      <div className="flex-1 mr-4">
                        <p className="font-medium text-sm flex items-center gap-1.5">
                          <BellRing className="h-4 w-4" />
                          Notificaciones push
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {pushPermission === 'granted'
                            ? 'Permiso activado. Recibirás alertas de nuevas suscripciones y renovaciones.'
                            : pushPermission === 'denied'
                              ? 'Permiso bloqueado por el navegador. Activa las notificaciones en la configuración del sitio.'
                              : 'Activa las notificaciones push para recibir alertas de nuevas suscripciones y renovaciones.'}
                        </p>
                      </div>
                      {pushPermission !== 'granted' ? (
                        <Button
                          size="sm"
                          variant={pushPermission === 'denied' ? 'outline' : 'default'}
                          disabled={isRequestingPush || pushPermission === 'denied'}
                          onClick={handleRequestPushPermission}
                        >
                          {isRequestingPush ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Activar'}
                        </Button>
                      ) : (
                        <Badge variant="success" className="bg-green-100 text-green-700">Activado</Badge>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {hasNutritionModule && (
              <div className="break-inside-avoid mb-6">
                {/* Reservas de citas con la nutricionista */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-primary">
                      <CalendarClock className="h-5 w-5" />
                      Reservas de citas (Nutrición)
                    </CardTitle>
                    <CardDescription>
                      Permite que tus clientas reserven su cita de control corporal desde un enlace público.
                      El horario disponible se edita en la pestaña Agenda de Nutrición.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">Reservas públicas</p>
                        <p className="text-xs text-muted-foreground">
                          Al activarlo se puede compartir el enlace de reserva con las clientas
                        </p>
                      </div>
                      <Switch
                        checked={nutritionBookingEnabled}
                        onCheckedChange={setNutritionBookingEnabled}
                      />
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Nombre de la nutricionista</Label>
                        <Input
                          value={nutritionistName}
                          onChange={(e) => setNutritionistName(e.target.value)}
                          placeholder="Ej: Lic. Ana Pérez"
                          disabled={isSavingNutritionSettings}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Antelación mínima (horas)</Label>
                        <Input
                          type="number"
                          min={0}
                          value={nutritionAdvanceHours}
                          onChange={(e) => setNutritionAdvanceHours(e.target.value)}
                          disabled={isSavingNutritionSettings}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Días hacia adelante que se pueden reservar</Label>
                        <Input
                          type="number"
                          min={1}
                          value={nutritionMaxDays}
                          onChange={(e) => setNutritionMaxDays(e.target.value)}
                          disabled={isSavingNutritionSettings}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Ventana de inasistencia (minutos)</Label>
                        <Input
                          type="number"
                          min={0}
                          value={nutritionGraceMinutes}
                          onChange={(e) => setNutritionGraceMinutes(e.target.value)}
                          disabled={isSavingNutritionSettings}
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">Política de cancelación</Label>
                      <Textarea
                        rows={2}
                        value={nutritionCancellationPolicy}
                        onChange={(e) => setNutritionCancellationPolicy(e.target.value)}
                        placeholder="Ej: Cancelar con al menos 12 horas de anticipación."
                        disabled={isSavingNutritionSettings}
                      />
                    </div>

                    <Button
                      onClick={handleSaveNutritionBooking}
                      disabled={isSavingNutritionSettings}
                    >
                      {isSavingNutritionSettings && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Guardar configuración
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}

            <div className="break-inside-avoid mb-6">
              {/* Security */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-primary">
                    <Shield className="h-5 w-5" />
                    Seguridad
                  </CardTitle>
                  <CardDescription>
                    Configuraciones de seguridad
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">Validación estricta de QR</p>
                      <p className="text-xs text-muted-foreground">
                        Verificar membresía activa en cada escaneo
                      </p>
                    </div>
                    <Switch
                      checked={strictQrValidation}
                      onCheckedChange={setStrictQrValidation}
                    />
                  </div>
                  <div className="flex items-center justify-between opacity-60">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">Registro de auditoría</p>
                        <Badge variant="outline" className="text-[9px] h-4 px-1 uppercase font-bold text-primary border-primary/20">Próximamente</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Guardar log de todas las acciones
                      </p>
                    </div>
                    <Switch disabled />
                  </div>
                  <div className="flex items-center justify-between opacity-60">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">Sesión única</p>
                        <Badge variant="outline" className="text-[9px] h-4 px-1 uppercase font-bold text-primary border-primary/20">Próximamente</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Cerrar sesiones anteriores al iniciar
                      </p>
                    </div>
                    <Switch disabled />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="break-inside-avoid mb-6">
              {/* Self Check-in Totem */}
              <Card className="border-primary/20 bg-primary/5 shadow-inner">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-primary">
                    <QrCode className="h-5 w-5" />
                    Totem de Auto Check-in
                  </CardTitle>
                  <CardDescription>
                    Permite que tus socios marquen asistencia desde su propio celular
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="bg-background/50 p-4 rounded-2xl border border-primary/10 flex flex-col items-center">
                    <div className="mb-4 bg-white p-2 rounded-xl shadow-lg">
                      {gym?.slug ? (
                        <img
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(window.location.origin + '/checkin/' + gym.slug)}`}
                          alt="QR Totem"
                          className="w-32 h-32"
                        />
                      ) : (
                        <div className="w-32 h-32 bg-muted animate-pulse rounded-lg flex items-center justify-center">
                          <QrCode className="h-8 w-8 text-muted-foreground/30" />
                        </div>
                      )}
                    </div>
                    <p className="text-xs font-bold text-center opacity-70 mb-4 max-w-[200px]">
                      Imprime este código y colócalo en tu recepción para que los socios lo escaneen al llegar.
                    </p>

                    <div className="w-full flex gap-2">
                      <Button
                        variant="outline"
                        className="flex-1 rounded-xl h-10 text-xs font-bold"
                        onClick={() => {
                          const url = window.location.origin + '/checkin/' + gym?.slug;
                          navigator.clipboard.writeText(url);
                          toast({ title: "Enlace copiado", description: "El URL del totem ha sido copiado al portapapeles." });
                        }}
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        COPIAR LINK
                      </Button>
                      <Button
                        className="flex-1 rounded-xl h-10 text-xs font-bold"
                        onClick={() => window.open('/checkin/' + gym?.slug, '_blank')}
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        ABRIR TOTEM
                      </Button>
                    </div>
                  </div>

                  <div className="bg-primary/10 p-4 rounded-xl space-y-2">
                    <p className="text-xs font-bold text-primary flex items-center gap-2">
                      <Check className="h-3 w-3" /> PERSISTENCIA DE IDENTIDAD
                    </p>
                    <p className="text-[10px] leading-relaxed opacity-80">
                      Tus clientes solo tendrán que identificarse la primera vez. El sistema recordará quiénes son cada vez que vuelvan a escanear el código.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="space-y-6">
            {/* Plan Selector */}
            {(isSuperAdmin || isGymAdmin) && (
              <Card className="lg:col-span-2 border-primary/20 shadow-sm overflow-hidden">
                <CardHeader className="bg-primary/5">
                  <CardTitle className="flex items-center gap-2 text-primary">
                    <Shield className="h-5 w-5" />
                    Plan de Suscripción
                  </CardTitle>
                  <CardDescription>
                    Selecciona el nivel de servicio que mejor se adapte a tu gimnasio
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {saasTiers.map((tier) => (
                      <div
                        key={tier.id}
                        onClick={() => setSelectedTierId(tier.id)}
                        className={cn(
                          "p-4 rounded-xl border transition-all cursor-pointer relative",
                          selectedTierId === tier.id
                            ? "border-primary bg-primary/5 ring-1 ring-primary"
                            : "border-border hover:border-primary/50 bg-card"
                        )}
                      >
                        {selectedTierId === tier.id && (
                          <div className="absolute top-2 right-2 h-5 w-5 bg-primary text-primary-foreground rounded-full flex items-center justify-center">
                            <Check className="h-3 w-3" />
                          </div>
                        )}
                        <h4 className="font-bold text-lg">{tier.name}</h4>
                        <div className="flex items-baseline gap-1 mt-1">
                          <span className="text-2xl font-black">${tier.price}</span>
                          <span className="text-xs text-muted-foreground">/mes</span>
                        </div>
                        <ul className="mt-4 space-y-2">
                          {tier.included_modules.slice(0, 4).map(modId => (
                            <li key={modId} className="text-[10px] flex items-center gap-2 text-muted-foreground">
                              <Check className="h-3 w-3 text-primary" />
                              {ALL_MODULES.find(m => m.module_id === modId)?.name || modId}
                            </li>
                          ))}
                          {tier.included_modules.length > 4 && (
                            <li className="text-[10px] text-muted-foreground italic">
                              + {tier.included_modules.length - 4} módulos más
                            </li>
                          )}
                        </ul>
                      </div>
                    ))}
                  </div>

                  {/* Billing Cycle Toggle */}
                  <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200/50">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <div className="flex-1">
                        <p className="text-sm font-bold text-green-900 flex items-center gap-2">
                          <Badge className="bg-green-600 text-white">-20%</Badge>
                          Pago anual con descuento
                        </p>
                        <p className="text-xs text-green-700 mt-1">
                          {billingCycle === 'annual' ? (
                            <>
                              Estás pagando <span className="font-bold">${annualDiscountedTotal.toFixed(2)}/año</span> en lugar de ${(currentTotal * 12).toFixed(2)}/año
                              <span className="block text-[10px] italic mt-0.5">Ahorrás ${annualSavings.toFixed(2)} al año vs. pago mensual.</span>
                            </>
                          ) : (
                            <>Pagá el año por adelantado y obtené un 20% de descuento. Ej: ${currentTotal.toFixed(2)}/mes → ${annualDiscountedTotal.toFixed(2)}/año (ahorro de ${annualSavings.toFixed(2)}).</>
                          )}
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button
                          size="sm"
                          variant={billingCycle === 'monthly' ? 'default' : 'outline'}
                          onClick={() => handleBillingCycleChange('monthly')}
                          disabled={isUpdatingBillingCycle}
                          className="h-9 font-bold"
                        >
                          Mensual
                        </Button>
                        <Button
                          size="sm"
                          variant={billingCycle === 'annual' ? 'default' : 'outline'}
                          onClick={() => handleBillingCycleChange('annual')}
                          disabled={isUpdatingBillingCycle}
                          className="h-9 font-bold"
                        >
                          {isUpdatingBillingCycle ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Anual'}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Price Optimization Suggestion */}
                  {(() => {
                    const currentTier = saasTiers.find(t => t.id === selectedTierId);
                    if (!currentTier || saasTiers.length === 0) return null;

                    // Optimization logic: Find the tier that results in the lowest TOTAL PRICE
                    // based on current selection of modules
                    const bestTierOption = saasTiers
                      .map(t => ({
                        tier: t,
                        totalPrice: calculatePriceForTier(t.id, enabledModules)
                      }))
                      .sort((a, b) => a.totalPrice - b.totalPrice)[0];

                    if (bestTierOption && bestTierOption.tier.id !== selectedTierId && (currentTotal - bestTierOption.totalPrice) > 0.01) {
                      const savings = currentTotal - bestTierOption.totalPrice;
                      const isUpgrade = Number(bestTierOption.tier.price) > Number(currentTier.price);

                      return (
                        <div className="mt-6 p-4 bg-primary/10 border border-primary/20 rounded-xl flex items-center gap-4 animate-in fade-in slide-in-from-top-2 duration-500">
                          <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                            <Icons.Zap className="h-5 w-5 text-primary" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-bold mb-1 text-primary">
                              {isUpgrade ? '¡Super Ahorro mediante Upgrade!' : '¡Optimiza tu costo mensual!'}
                            </p>
                            <p className="text-xs text-muted-foreground font-medium leading-relaxed">
                              Hemos detectado que con tu selección actual de módulos, el plan <span className="font-bold text-foreground">{bestTierOption.tier.name}</span> es más económico.
                              Tu total bajaría de ${currentTotal.toFixed(2)} a <span className="font-bold text-foreground font-mono">${bestTierOption.totalPrice.toFixed(2)}</span>.
                            </p>
                            <Button
                              variant="link"
                              size="sm"
                              onClick={() => setSelectedTierId(bestTierOption.tier.id)}
                              className="p-0 h-auto text-[10px] text-primary font-bold mt-2 uppercase tracking-widest hover:no-underline flex items-center gap-1 group"
                            >
                              Aplicar recomendación <Icons.ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1" />
                            </Button>
                          </div>
                          <div className="hidden md:flex flex-col items-end">
                            <span className="text-[10px] font-bold text-muted-foreground uppercase whitespace-nowrap">Ahorras</span>
                            <span className="text-2xl font-black text-green-500">-${savings.toFixed(2)}</span>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Modules & Addons (Admin only) */}
          {(isSuperAdmin || isGymAdmin) && (
            <Card className="lg:col-span-2 border-primary/20 shadow-sm overflow-hidden">
              <div className="bg-primary/5 px-4 md:px-6 py-4 flex flex-col lg:flex-row justify-between items-start lg:items-center border-b border-primary/10 gap-4">
                <div className="space-y-1">
                  <CardTitle className="flex items-center gap-2 text-primary">
                    <Database className="h-5 w-5" />
                    Módulos y Addons
                  </CardTitle>
                  <CardDescription>
                    Personaliza tu experiencia con funcionalidades extra
                  </CardDescription>
                </div>
                <div className="bg-background/80 backdrop-blur px-3 md:px-4 py-2 rounded-xl border border-primary/20 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-4 w-full lg:w-auto">
                  <div className="flex flex-col flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold text-muted-foreground uppercase">
                        {hasSubscriptionChanges ? 'Nueva Mensualidad:' : 'Mensualidad Actual:'}
                      </span>
                      <span className="text-base md:text-lg font-black text-primary">${currentTotal.toFixed(2)}</span>
                    </div>
                    {gym?.credit_balance !== 0 && (
                      <div className={cn(
                        "flex items-center gap-1 text-[10px] font-bold whitespace-nowrap",
                        (gym?.credit_balance || 0) > 0 ? "text-green-500" : "text-blue-500"
                      )}>
                        {(gym?.credit_balance || 0) > 0 ? (
                          <>
                            <Icons.Check className="h-3 w-3" />
                            <span className="truncate">Saldo: ${(gym?.credit_balance || 0).toFixed(2)}</span>
                          </>
                        ) : (
                          <>
                            <Icons.AlertTriangle className="h-3 w-3" />
                            <span className="truncate">Adeudo: ${Math.abs(gym?.credit_balance || 0).toFixed(2)}</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  {hasSubscriptionChanges && (
                    <Button
                      size="sm"
                      disabled={isUpdatingModules}
                      onClick={handleUpdateAddons}
                      className="rounded-full px-4 md:px-6 h-9 md:h-10 font-bold shadow-lg shadow-primary/20 shrink-0"
                    >
                      {isUpdatingModules ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : 'Confirmar'}
                    </Button>
                  )}
                </div>
              </div>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {ALL_MODULES.filter(m => m.module_id !== 'webhooks').map((module) => {
                    const currentTier = saasTiers.find(t => t.id === selectedTierId) || gym?.tier;
                    const isIncludedInPlan = currentTier?.included_modules?.includes(module.module_id);
                    const isEnabled = enabledModules.includes(module.module_id) || isIncludedInPlan;
                    const Icon = (Icons as any)[module.icon] || Database;

                    return (
                      <div
                        key={module.module_id}
                        onClick={() => toggleModule(module.module_id)}
                        className={cn(
                          "p-4 rounded-xl border transition-all cursor-pointer group flex flex-col justify-between h-full relative overflow-hidden",
                          isEnabled
                            ? "border-primary bg-primary/5 shadow-inner"
                            : "border-border bg-muted/20 grayscale hover:grayscale-0 hover:bg-muted/40",
                          isIncludedInPlan && "cursor-default opacity-90 ring-1 ring-primary/20"
                        )}
                      >
                        {isEnabled && (
                          <div className="absolute top-0 right-0 p-2 bg-primary text-primary-foreground rounded-bl-xl shadow-sm">
                            <Check className="h-3 w-3" />
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-3 mb-3">
                            <div className={cn(
                              "w-10 h-10 rounded-lg flex items-center justify-center transition-colors shadow-sm",
                              isEnabled ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                            )}>
                              <Icon className="h-5 w-5" />
                            </div>
                            <div className="flex flex-col">
                              <h4 className="font-bold text-sm tracking-tight">{module.name}</h4>
                              <span className={cn(
                                "text-[10px] font-bold uppercase",
                                isEnabled ? "text-primary" : "text-muted-foreground"
                              )}>
                                {isIncludedInPlan ? (
                                  <span className="flex items-center gap-1">
                                    <Shield className="h-3 w-3" /> INCLUIDO
                                  </span>
                                ) : (
                                  module.price > 0 ? `+$${module.price} / mes` : 'Incluido'
                                )}
                              </span>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mb-4 line-clamp-2">
                            {module.description}
                          </p>
                        </div>
                        <div className="flex items-center justify-between mt-auto">
                          <Badge variant={isEnabled ? "default" : "outline"} className="text-[9px] uppercase h-5 font-bold">
                            {isEnabled ? 'Activo' : 'Desactivado'}
                          </Badge>
                          {!isEnabled && module.price > 0 && (
                            <span className="text-[10px] text-primary font-bold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Plus className="h-3 w-3" /> Añadir addon
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* WhatsApp AI Addon */}
                  {(() => {
                    const currentTier = saasTiers.find(t => t.id === selectedTierId) || gym?.tier;
                    const isIncludedInPlan = currentTier?.included_modules?.includes('webhooks');
                    const status = isIncludedInPlan ? 'approved' : whatsappAddon?.status;

                    return (
                      <div
                        className={cn(
                          "p-4 rounded-xl border transition-all flex flex-col justify-between h-full relative overflow-hidden",
                          status === 'approved' ? "border-primary bg-primary/5 shadow-inner" : "border-border bg-muted/20",
                          isIncludedInPlan && "ring-1 ring-primary/20",
                          status === 'deactivated' && "border-yellow-500/50 bg-yellow-500/5 font-medium"
                        )}
                      >
                        {status === 'approved' && (
                          <div className="absolute top-0 right-0 p-2 bg-primary text-primary-foreground rounded-bl-xl shadow-sm">
                            <Check className="h-3 w-3" />
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-3 mb-3">
                            <div className={cn(
                              "w-10 h-10 rounded-lg flex items-center justify-center shadow-sm",
                              status === 'approved' ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
                              status === 'deactivated' && "bg-yellow-500 text-white"
                            )}>
                              <MessageSquare className="h-5 w-5" />
                            </div>
                            <div className="flex flex-col">
                              <h4 className="font-bold text-sm text-primary">WhatsApp AI Assistant</h4>
                              <span className="text-[10px] text-primary font-bold">
                                {isIncludedInPlan ? (
                                  <span className="flex items-center gap-1 uppercase">
                                    <Shield className="h-3 w-3" /> INCLUIDO EN {currentTier?.name}
                                  </span>
                                ) : (
                                  status === 'deactivated' ? (
                                    <span className="text-yellow-600 font-black">REQ. REACTIVACIÓN (+$10)</span>
                                  ) : (
                                    <span className="flex flex-col">
                                      <span>+$25 / mes</span>
                                      {whatsappAddon?.status === 'rejected' && <span className="text-red-500 text-[8px] uppercase">Rechazado anteriormente</span>}
                                    </span>
                                  )
                                )}
                              </span>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
                            Conversaciones inteligentes 24/7 con tus clientes. Mejora la retención y ventas con IA.
                          </p>
                        </div>

                        <div className="flex flex-col gap-2 mt-auto">
                          {status === 'approved' ? (
                            <div className="space-y-2">
                              <Badge variant="default" className="w-full justify-center text-[10px] uppercase font-black py-1 bg-green-500 hover:bg-green-600">
                                Servicio Activo
                              </Badge>
                              <div className="flex gap-2">
                                <Button variant="outline" size="sm" className="flex-1 text-[10px] h-8 font-bold uppercase transition-all hover:bg-primary hover:text-white" onClick={() => window.location.href = '/whatsapp'}>
                                  Abrir Chat
                                </Button>
                                {(isSuperAdmin || isGymAdmin) && !isIncludedInPlan && (
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    title="Desactivar"
                                    onClick={() => setIsDeactivatingDialogOpen(true)}
                                    className="h-8 w-8 border-red-500/20 text-red-500 hover:bg-red-500 hover:text-white"
                                  >
                                    <Ban className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ) : status === 'pending_approval' ? (
                            <div className="space-y-2">
                              <Badge variant="outline" className="w-full justify-center text-[10px] uppercase h-6 font-bold border-yellow-500 text-yellow-600">
                                Pendiente de aprobación
                              </Badge>
                              {isSuperAdmin && (
                                <div className="flex gap-1">
                                  <Button size="sm" className="flex-1 h-7 text-[9px] bg-green-600 hover:bg-green-700" onClick={() => handleApproveAddon(whatsappAddon.id, 'approved')}>Aprobar</Button>
                                  <Button size="sm" variant="destructive" className="flex-1 h-7 text-[9px]" onClick={() => handleApproveAddon(whatsappAddon.id, 'rejected')}>Rechazar</Button>
                                </div>
                              )}
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              onClick={handleRequestWhatsapp}
                              disabled={isLoadingAddon}
                              className={cn(
                                "w-full text-[10px] font-bold h-9 uppercase tracking-widest transition-all",
                                status === 'deactivated' ? "bg-yellow-500 hover:bg-yellow-600 text-white shadow-lg shadow-yellow-500/20" : "bg-primary"
                              )}
                            >
                              {isLoadingAddon ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              ) : status === 'deactivated' ? (
                                <>
                                  <Plus className="h-3 w-3 mr-2" /> Reactivar ($10)
                                </>
                              ) : (
                                'Solicitar Activación'
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {JSON.stringify(enabledModules) !== JSON.stringify(gym?.modules_enabled) && (
                  <div className="mt-6 p-4 rounded-xl bg-orange-500/5 border border-orange-500/20 flex items-start gap-3 animate-in fade-in slide-in-from-top-2">
                    <AlertTriangle className="h-5 w-5 text-orange-500 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-sm font-bold text-orange-700">Cambios pendientes por guardar</p>
                      <p className="text-xs text-orange-600 leading-relaxed">
                        Has modificado tus addons. Al guardar, se actualizará tu cargo mensual de ${calculateTotalPrice(gym?.modules_enabled || []).toFixed(2)} a ${currentTotal.toFixed(2)}.
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="membresias" className="mt-0">
          <Card className="border-none shadow-sm overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between pb-4 border-b">
              <div>
                <CardTitle className="flex items-center gap-2 text-primary">
                  <Ticket className="h-5 w-5" />
                  Tipos de Membresías
                </CardTitle>
                <CardDescription>
                  Define hasta 8 planes diferentes para tus clientes ({plans.length}/8)
                </CardDescription>
              </div>
              <Button
                onClick={() => {
                  setEditingPlan({});
                  setIsPlanDialogOpen(true);
                }}
                disabled={plans.length >= 8}
                variant="gradient"
                className="rounded-full px-6 font-bold"
              >
                <Plus className="h-4 w-4 mr-2" /> Nuevo Plan
              </Button>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {isLoadingPlans ? (
                  Array(4).fill(0).map((_, i) => (
                    <div key={i} className="h-32 rounded-xl bg-muted animate-pulse" />
                  ))
                ) : plans.length > 0 ? (
                  plans.map((plan) => {
                    const isGlobal = !plan.gym_id;

                    return (
                      <div key={plan.id} className={cn(
                        "p-4 rounded-xl border bg-card hover:border-primary/50 transition-all group relative overflow-hidden",
                        isGlobal && "border-primary/30 ring-1 ring-primary/10"
                      )}>
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex flex-col gap-1 pr-8">
                            <h4 className="font-bold text-sm truncate">{plan.name}</h4>
                            <div className="flex gap-1 flex-wrap">
                              {isGlobal && <Badge variant="outline" className="text-[10px] w-fit h-4 text-primary border-primary/20">Global</Badge>}
                              {plan.is_private && <Badge variant="secondary" className="text-[10px] w-fit h-4 bg-amber-500/10 text-amber-600 border-amber-500/20">Privado</Badge>}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-primary"
                              onClick={() => {
                                setEditingPlan(plan);
                                setIsPlanDialogOpen(true);
                              }}
                              disabled={isGlobal && !isSuperAdmin}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              onClick={() => handleDeletePlan(plan.id)}
                              disabled={isGlobal && !isSuperAdmin}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-end justify-between mt-4">
                          <div className="text-xl font-black text-primary">${plan.price}</div>
                          <Badge variant="secondary" className="text-[10px] font-bold">{plan.duration_days} d</Badge>
                        </div>
                        {plan.description && (
                          <p className="text-[10px] text-muted-foreground mt-2 italic truncate">{plan.description}</p>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="col-span-full py-20 text-center">
                    <div className="h-16 w-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4 grayscale opacity-20">
                      <Ticket className="h-8 w-8" />
                    </div>
                    <p className="text-muted-foreground font-medium">No se encontraron planes.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {isSuperAdmin && (
          <TabsContent value="sistema" className="mt-0">
            <Card className="border-primary/20 bg-primary/5">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-primary text-primary-foreground shadow-lg shadow-primary/20">
                    <SettingsIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-black">Configuración del Sistema</CardTitle>
                    <CardDescription>
                      Gestiona las llaves de API globales para Twilio y OpenAI
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <form onSubmit={handleUpdateIntegrations} className="space-y-6 max-w-2xl">
                  <div className="grid gap-6">
                    <div className="space-y-2">
                      <Label className="text-sm font-bold flex items-center gap-2">
                        OpenAI API Key for AI Assistant
                        <Badge variant="outline" className="text-[9px] font-mono">openai_api_key</Badge>
                      </Label>
                      <Input
                        type="password"
                        value={openaiApiKey}
                        onChange={(e) => setOpenaiApiKey(e.target.value)}
                        placeholder="sk-..."
                        className="bg-background border-primary/20 focus:border-primary"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-bold flex items-center gap-2">
                        Twilio Account SID for WhatsApp
                        <Badge variant="outline" className="text-[9px] font-mono">twilio_account_sid</Badge>
                      </Label>
                      <Input
                        value={twilioAccountSid}
                        onChange={(e) => setTwilioAccountSid(e.target.value)}
                        placeholder="AC..."
                        className="bg-background border-primary/20 focus:border-primary"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-bold flex items-center gap-2">
                        Twilio Auth Token for WhatsApp
                        <Badge variant="outline" className="text-[9px] font-mono">twilio_auth_token</Badge>
                      </Label>
                      <Input
                        type="password"
                        value={twilioAuthToken}
                        onChange={(e) => setTwilioAuthToken(e.target.value)}
                        placeholder="Token..."
                        className="bg-background border-primary/20 focus:border-primary"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-bold flex items-center gap-2">
                        Twilio Phone Number (WhatsApp number)
                        <Badge variant="outline" className="text-[9px] font-mono">twilio_phone_number</Badge>
                      </Label>
                      <Input
                        value={twilioPhoneNumber}
                        onChange={(e) => setTwilioPhoneNumber(e.target.value)}
                        placeholder="+1..."
                        className="bg-background border-primary/20 focus:border-primary"
                      />
                    </div>
                  </div>

                  <div className="pt-4 flex justify-end">
                    <Button type="submit" variant="gradient" className="font-bold px-10 rounded-full" disabled={isSavingIntegrations}>
                      {isSavingIntegrations ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Guardando llaves...
                        </>
                      ) : 'Actualizar Llaves del Sistema'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* New WhatsApp Deactivation Dialog */}
      <Dialog open={isDeactivatingDialogOpen} onOpenChange={setIsDeactivatingDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <AlertTriangle className="h-5 w-5" /> Confirmar Desactivación
            </DialogTitle>
            <DialogDescription className="pt-2 font-medium">
              ¿Estás seguro de que deseas desactivar el Asistente de WhatsApp?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-xs text-muted-foreground leading-relaxed">
              Al desactivar este servicio, tu gimnasio perderá los siguientes beneficios inmediatamente:
            </p>
            <ul className="space-y-2">
              {[
                'Atención automática 24/7 de consultas frecuentes',
                'Asistente de ventas inteligente para nuevos leads',
                'Recordatorios automáticos de membresías por expirar',
                'Confirmación instantánea de pagos vía WhatsApp'
              ].map((benefit, i) => (
                <li key={i} className="flex items-start gap-2 text-[11px] text-foreground/80">
                  <Icons.XCircle className="h-3.5 w-3.5 text-red-500 mt-0.5 shrink-0" />
                  {benefit}
                </li>
              ))}
            </ul>
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <p className="text-[10px] text-yellow-700 font-bold leading-tight">
                <Icons.AlertCircle className="h-3 w-3 inline mr-1" />
                IMPORTANTE: Si decides reactivar este servicio en el futuro, se aplicará un cargo administrativo único de <span className="underline">$10.00 USD</span> por reactivación técnica.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setIsDeactivatingDialogOpen(false)} disabled={isDeactivatingWhatsapp}>
              Mantener Servicio
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeactivateWhatsapp}
              disabled={isDeactivatingWhatsapp}
            >
              {isDeactivatingWhatsapp ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : 'Sí, Desactivar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscription Change Confirmation Dialog */}
      <Dialog open={isConfirmingSubscriptionChange} onOpenChange={setIsConfirmingSubscriptionChange}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-primary">
              <Shield className="h-5 w-5" /> Confirmar Cambios en Suscripción
            </DialogTitle>
            <DialogDescription className="pt-2 font-medium">
              Por favor, revisa el resumen de los cambios aplicados a tu plan.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-6">
            {/* Price Comparison */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1 p-3 rounded-lg bg-muted/50 border">
                <p className="text-[10px] font-bold text-muted-foreground uppercase">Mensualidad Actual</p>
                <p className="text-lg font-black">${(gym?.price || 0).toFixed(2)}</p>
              </div>
              <div className="space-y-1 p-3 rounded-lg bg-primary/5 border border-primary/20">
                <p className="text-[10px] font-bold text-primary uppercase">Nueva Mensualidad</p>
                <p className="text-lg font-black text-primary">${currentTotal.toFixed(2)}</p>
              </div>
            </div>

            {/* Proration Detail */}
            {proratedCharge !== 0 && (
              <div className={cn(
                "p-4 rounded-xl border flex items-center gap-3",
                proratedCharge > 0 ? "bg-green-500/10 border-green-500/20" : "bg-blue-500/10 border-blue-500/20"
              )}>
                <div className={cn(
                  "h-10 w-10 rounded-full flex items-center justify-center shrink-0",
                  proratedCharge > 0 ? "bg-green-500/20 text-green-600" : "bg-blue-500/20 text-blue-600"
                )}>
                  {proratedCharge > 0 ? <Icons.Plus className="h-5 w-5" /> : <Icons.Minus className="h-5 w-5" />}
                </div>
                <div>
                  <p className={cn(
                    "text-sm font-bold",
                    proratedCharge > 0 ? "text-green-700" : "text-blue-700"
                  )}>
                    {proratedCharge > 0 ? 'Monto a pagar hoy' : 'Crédito a tu favor'}
                  </p>
                  <p className="text-xs font-medium opacity-80 leading-tight">
                    {proratedCharge > 0
                      ? `Se realizará un cargo inmediato de $${proratedCharge.toFixed(2)} por los días restantes del ciclo.`
                      : `Se aplicará un crédito de $${Math.abs(proratedCharge).toFixed(2)} que se descontará de tu próxima factura.`}
                  </p>
                </div>
              </div>
            )}

            {/* Breakdown of Changes */}
            <div className="space-y-3">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Resumen de cambios</p>
              <div className="space-y-2 max-h-[150px] overflow-y-auto pr-2 custom-scrollbar">
                {/* Tier Change */}
                {selectedTierId !== gym?.tier_id && (
                  <div className="flex items-center justify-between text-xs py-1 border-b border-border/50">
                    <span className="text-muted-foreground">Cambio de Plan</span>
                    <span className="font-bold">{saasTiers.find(t => t.id === gym?.tier_id)?.name} → {saasTiers.find(t => t.id === selectedTierId)?.name}</span>
                  </div>
                )}
                {/* Added Modules */}
                {enabledModules.filter(m => !gym?.modules_enabled?.includes(m)).map(mId => (
                  <div key={mId} className="flex items-center justify-between text-xs py-1 border-b border-border/50">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                      <span className="text-muted-foreground">Añadido: {ALL_MODULES.find(m => m.module_id === mId)?.name}</span>
                    </div>
                    <span className="font-bold text-green-600">+${ALL_MODULES.find(m => m.module_id === mId)?.price || 0}</span>
                  </div>
                ))}
                {/* Removed Modules */}
                {gym?.modules_enabled?.filter(m => !enabledModules.includes(m)).map(mId => (
                  <div key={mId} className="flex items-center justify-between text-xs py-1 border-b border-border/50">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-red-500" />
                      <span className="text-muted-foreground">Removido: {ALL_MODULES.find(m => m.module_id === mId)?.name}</span>
                    </div>
                    <span className="font-bold text-red-600">-${ALL_MODULES.find(m => m.module_id === mId)?.price || 0}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setIsConfirmingSubscriptionChange(false)} disabled={isUpdatingModules}>
              Cancelar
            </Button>
            <Button
              variant="gradient"
              onClick={executeSubscriptionChange}
              disabled={isUpdatingModules}
              className="font-bold shadow-lg shadow-primary/20"
            >
              {isUpdatingModules ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                proratedCharge > 0 ? `Confirmar y Pagar $${proratedCharge.toFixed(2)}` : 'Confirmar Cambios'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan Edit Dialog */}
      <Dialog open={isPlanDialogOpen} onOpenChange={setIsPlanDialogOpen}>
        <DialogContent className="sm:max-w-[425px] rounded-3xl">
          <DialogHeader>
            <DialogTitle>{editingPlan?.id ? 'Editar Plan' : 'Nuevo Plan de Membresía'}</DialogTitle>
            <DialogDescription>
              Configura los detalles de este nivel de membresía.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="plan-name">Nombre del Plan</Label>
              <Input
                id="plan-name"
                placeholder="Ej: Mensual Premium"
                value={editingPlan?.name || ''}
                onChange={(e) => setEditingPlan({ ...editingPlan, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="plan-price">Precio ($)</Label>
                <Input
                  id="plan-price"
                  type="number"
                  placeholder="29.99"
                  value={editingPlan?.price || ''}
                  onChange={(e) => setEditingPlan({ ...editingPlan, price: parseFloat(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="plan-duration">Duración (Días)</Label>
                <Input
                  id="plan-duration"
                  type="number"
                  placeholder="30"
                  value={editingPlan?.duration_days || ''}
                  onChange={(e) => setEditingPlan({ ...editingPlan, duration_days: parseInt(e.target.value) })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="plan-sessions">Sesiones Totales</Label>
              <Input
                id="plan-sessions"
                type="number"
                placeholder="Ilimitado"
                value={editingPlan?.total_sessions || ''}
                onChange={(e) => setEditingPlan({ ...editingPlan, total_sessions: e.target.value ? parseInt(e.target.value) : null })}
              />
              <p className="text-[10px] text-muted-foreground italic">Vacío para ilimitado (ej: El plan de 15 días con 6 usos tendría 15 en Duración y 6 aquí)</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="plan-desc">Descripción (Opcional)</Label>
              <Input
                id="plan-desc"
                placeholder="Ej: Acceso total a todas las áreas"
                value={editingPlan?.description || ''}
                onChange={(e) => setEditingPlan({ ...editingPlan, description: e.target.value })}
              />
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30 border border-dashed">
              <div className="space-y-0.5">
                <Label htmlFor="plan-private" className="text-sm font-bold">Plan Privado</Label>
                <p className="text-[10px] text-muted-foreground">Ocultar de la página de registro público</p>
              </div>
              <Switch
                id="plan-private"
                checked={editingPlan?.is_private || false}
                onCheckedChange={(checked) => setEditingPlan({ ...editingPlan, is_private: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPlanDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSavePlan}
              disabled={isSavingPlan}
              variant="gradient"
            >
              {isSavingPlan ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : 'Guardar Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout >
  );
}
